import type { ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

/**
 * A form that slides in from the side: new appointment, edit details,
 * reschedule. tperent's dialogs are the same shape — a sheet from the bottom
 * on a phone, from the right on anything wider — and the console's drawer
 * already is the right-hand half of that, so this wears its classes.
 *
 * It may open over the appointment drawer. Both are portalled, and the later
 * one lands on top, which is the order a stylist opened them in.
 */
export function FormSheet({
  open,
  onOpenChange,
  title,
  description,
  onOpenAutoFocus,
  children,
}: {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly title: string
  readonly description?: string
  /**
   * Where the cursor lands when the sheet opens. Left out, it is the first
   * control, which is Radix's own choice; call `preventDefault()` on the
   * event and focus another.
   */
  readonly onOpenAutoFocus?: (event: Event) => void
  readonly children: ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="st-overlay" />
        <Dialog.Content className="st-drawer st-root" aria-describedby={undefined} onOpenAutoFocus={onOpenAutoFocus}>
          <header className="flex items-start gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--st-rule)' }}>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="st-display text-xl">{title}</Dialog.Title>
              {description ? <p className="st-meta mt-1 text-xs">{description}</p> : null}
            </div>
            <Dialog.Close asChild>
              <button type="button" className="st-iconbtn" aria-label="Close">
                <X size={16} aria-hidden />
              </button>
            </Dialog.Close>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/** One labelled control, with its error under it. */
export function FormField({
  label,
  error,
  hint,
  children,
}: {
  readonly label: string
  readonly error?: string | undefined
  readonly hint?: string
  readonly children: ReactNode
}) {
  return (
    <label className="mb-3 flex flex-col gap-1">
      <span className="st-eyebrow">{label}</span>
      {children}
      {hint && !error ? <span className="st-meta text-xs">{hint}</span> : null}
      {error ? (
        <span className="text-xs" style={{ color: '#8d3f2c' }} role="alert">
          {error}
        </span>
      ) : null}
    </label>
  )
}
